import React from 'react';
import { Popover } from '@headlessui/react';
import { UsersIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';

interface GuestCount {
  adults: number;
  children: number;
  infants: number;
}

interface GuestSelectorProps {
  guests: GuestCount;
  onChange: (guests: GuestCount) => void;
  className?: string;
}

const GuestSelector: React.FC<GuestSelectorProps> = ({
  guests,
  onChange,
  className = '',
}) => {
  const updateGuestCount = (type: keyof GuestCount, increment: boolean) => {
    const newGuests = { ...guests };
    if (increment) {
      newGuests[type] = (newGuests[type] || 0) + 1;
    } else if ((newGuests[type] || 0) > 0) {
      newGuests[type] = (newGuests[type] || 0) - 1;
    }
    onChange(newGuests);
  };

  const totalGuests = guests.adults + guests.children + guests.infants;

  return (
    <Popover className={`relative ${className}`}>
      <Popover.Button className="w-full flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full hover:border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-airbnb-red">
        <UsersIcon className="h-5 w-5 text-gray-400" />
        <div className="flex flex-col text-left">
          <span className="text-sm font-medium text-gray-900">Guests</span>
          <span className="text-xs text-gray-500">
            {totalGuests} {totalGuests === 1 ? 'guest' : 'guests'}
          </span>
        </div>
      </Popover.Button>

      <Popover.Panel className="absolute z-10 w-80 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium text-gray-900">Adults</h3>
              <p className="text-sm text-gray-500">Ages 13 or above</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => updateGuestCount('adults', false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
                disabled={guests.adults <= 1}
              >
                <MinusIcon className="h-4 w-4 text-gray-600" />
              </button>
              <span className="w-8 text-center text-gray-900">{guests.adults}</span>
              <button
                onClick={() => updateGuestCount('adults', true)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <PlusIcon className="h-4 w-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </Popover.Panel>
    </Popover>
  );
};

export default GuestSelector;